import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { API } from '@/shared/api';
import { getIsUserLeftProjectPage } from '@/shared/lib/location';
import { TServerSegment, TUpdatingSegmentData } from '@/shared/types/Editor';
import { Project } from '@/shared/types/Project';

import { RootState } from '../../index';
import {
  markTranscriptVersionAutoSaveDirty,
  setIsVoiceSuggesting,
  updSegment,
  updSegmentFromServer,
} from '../EditorSlice';
import { getRevertedSegmentData, getUnsupersededChangedKeys } from '../utils/getRevertedSegmentData';

type UpdateSegments = {
  changedData: TUpdatingSegmentData[];
  project?: Project;
};

const BATCH_SIZE = 50;

/**
 * Разбивает массив на батчи указанного размера
 * @param array - исходный массив
 * @param batchSize - размер батча
 * @returns массив батчей
 */
const chunkArray = <T>(array: T[], batchSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    chunks.push(array.slice(i, i + batchSize));
  }
  return chunks;
};

export const updateSegments = createAsyncThunk<void, UpdateSegments, { state: RootState }>(
  'editor/updateSegment',
  async ({ changedData, project }: UpdateSegments, { dispatch, getState }) => {
    try {
      if (!project) {
        return;
      }

      // Разбиваем данные на батчи для предотвращения ошибки 413 "Payload Too Large"
      const batches = chunkArray(changedData, BATCH_SIZE);
      const allUpdatedSegments: TServerSegment[] = [];

      // Обрабатываем все батчи параллельно
      const batchPromises = batches.map(async (batch) => {
        const { segments: updatedSegments } = await API.updateSegments(project.id, batch);
        return updatedSegments;
      });

      const batchResults = await Promise.all(batchPromises);

      // Объединяем результаты всех батчей
      batchResults.forEach((segments) => {
        allUpdatedSegments.push(...segments);
      });

      if (!allUpdatedSegments.length) {
        throw new Error();
      }

      const isUserLeftProject = getIsUserLeftProjectPage(project.id);

      if (isUserLeftProject) {
        return;
      }

      allUpdatedSegments.forEach((segment) => {
        dispatch(updSegmentFromServer(segment));
      });
      dispatch(markTranscriptVersionAutoSaveDirty());
    } catch {
      const {
        editor: { segmentsDataToSave, segmentsFromServerMap },
      } = getState();

      const failedSegments = changedData
        .map((segment) => ({
          segment,
          unsupersededChangedKeys: getUnsupersededChangedKeys(segment, segmentsDataToSave[segment.id]),
        }))
        .filter(({ unsupersededChangedKeys }) => unsupersededChangedKeys.length > 0);

      if (!failedSegments.length) {
        return;
      }

      enqueueSnackbar("We couldn't save the segment. Please try again", {
        autoHideDuration: 6000,
      });

      failedSegments.forEach(({ segment, unsupersededChangedKeys }) => {
        const segmentFromServer = segmentsFromServerMap[segment.id];
        const revertedSegmentData = getRevertedSegmentData(segment.id, unsupersededChangedKeys, segmentFromServer);

        // Add isSavingError to the segment to highlight it
        dispatch(updSegment({ id: segment.id, isSavingError: true }));

        // Revert only failed fields that do not have a newer queued save.
        if (revertedSegmentData) {
          dispatch(updSegment(revertedSegmentData));
        }

        if (unsupersededChangedKeys.includes('speaker')) {
          dispatch(setIsVoiceSuggesting(false));
        }
      });
    }
  },
);
